import { AlertTriangle, Bot, ChevronDown, ChevronRight, FileDown, UserCheck } from "lucide-react";
import { useState } from "react";
import { api } from "@/services/api";
import type { ReviewResult, ReviewSection } from "../types";
import { InlineCitation } from "./ui/inline-citation";

interface ReviewResultsProps {
  result: ReviewResult;
  variant?: "ai" | "final";
  /** When the assessor confirmed the review (final variant byline) */
  assessorReviewedAt?: Date | null;
}

export default function ReviewResults({
  result,
  variant = "ai",
  assessorReviewedAt,
}: ReviewResultsProps) {
  // Start with all sections expanded
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(result.sections?.map((s) => s.name) || [])
  );

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionName)) {
        next.delete(sectionName);
      } else {
        next.add(sectionName);
      }
      return next;
    });
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return "Excellent";
    if (score >= 60) return "Good";
    return "Needs Improvement";
  };

  const getScoreColorClass = (score: number, scale: "100" | "20" = "100") => {
    const threshold = scale === "100" ? { high: 80, mid: 60 } : { high: 16, mid: 12 };
    if (score >= threshold.high) return "text-green-600";
    if (score >= threshold.mid) return "text-amber-600";
    return "text-red-600";
  };

  const formatTimestamp = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  };

  const isFinal = variant === "final";
  const title = isFinal ? "Final Review" : "AI Feedback";
  const Icon = isFinal ? UserCheck : Bot;

  const getBandColorClass = (band?: string) => {
    switch (band) {
      case "Professional":
        return "bg-green-100 text-green-700 border-green-300";
      case "Advanced":
        return "bg-blue-100 text-blue-700 border-blue-300";
      case "Competent":
        return "bg-amber-100 text-amber-700 border-amber-300";
      case "Emerging":
        return "bg-red-100 text-red-700 border-red-300";
      default:
        return "bg-gray-100 text-gray-600 border-gray-300";
    }
  };

  const renderSection = (section: ReviewSection) => {
    const isExpanded = expandedSections.has(section.name);

    if (section.errored) {
      return (
        <div
          key={section.name}
          className="border border-amber-300 bg-amber-50 rounded-xl overflow-hidden"
        >
          <div className="flex items-start gap-3 p-4">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-gray-900 font-medium">{section.name}</span>
                <span className="text-xs font-medium text-amber-700">Could not assess</span>
              </div>
              <p className="text-sm text-amber-800 mt-1">
                This criterion could not be evaluated and was excluded from the score. Please retry
                the review.
              </p>
              {section.error_message && (
                <p className="text-xs text-amber-700/80 mt-2 font-mono break-all">
                  {section.error_message}
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={section.name}
        className="rounded-xl border border-content-border bg-card overflow-hidden"
      >
        <button
          onClick={() => toggleSection(section.name)}
          className="w-full flex items-center justify-between p-4 hover:bg-content-subtle transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
            <span className="text-gray-900 font-medium">{section.name}</span>
          </div>
          <div className="flex items-center gap-2">
            {section.band && (
              <span
                className={`text-xs px-2 py-0.5 rounded-full border ${getBandColorClass(section.band)}`}
              >
                {section.band}
              </span>
            )}
            {isFinal && (
              <span className={`font-bold ${getScoreColorClass(section.score, "20")}`}>
                {section.score}/20
              </span>
            )}
          </div>
        </button>

        {isExpanded && (
          <div className="px-4 pb-4 pt-2 border-t border-content-border/60">
            <div className="space-y-4 pl-7">
              {/* Diagnostic explanation */}
              {section.diagnostic_explanation && (
                <p className="text-sm text-gray-600 italic">
                  <InlineCitation text={section.diagnostic_explanation} />
                </p>
              )}

              {/* Observable features detected */}
              {section.observable_features_detected &&
                section.observable_features_detected.length > 0 && (
                  <div>
                    <h5 className="text-sm font-semibold text-teal-700 mb-2">
                      Observable Features
                    </h5>
                    <ul className="space-y-1.5">
                      {section.observable_features_detected.map((feature, idx) => (
                        <li
                          key={idx}
                          className="text-sm text-gray-700 pl-3 border-l-2 border-teal-500"
                        >
                          <InlineCitation text={feature} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {/* Missing features for next band */}
              {section.missing_features_for_next_band &&
                section.missing_features_for_next_band.length > 0 && (
                  <div>
                    <h5 className="text-sm font-semibold text-amber-700 mb-2">
                      To Reach Next Band
                    </h5>
                    <ul className="space-y-1.5">
                      {section.missing_features_for_next_band.map((feature, idx) => (
                        <li
                          key={idx}
                          className="text-sm text-gray-700 pl-3 border-l-2 border-amber-500"
                        >
                          <InlineCitation text={feature} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Icon className={`w-5 h-5 ${isFinal ? "text-green-600" : "text-blue-600"}`} />
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <span
            className={`ml-1 rounded-full border px-2.5 py-1 text-[10.5px] font-bold tracking-[.1em] uppercase ${
              isFinal
                ? "border-green-200 bg-green-50 text-green-700"
                : "border-blue-200 bg-blue-50 text-blue-700"
            }`}
          >
            {isFinal ? "✓ Assessor Confirmed" : "AI Reviewed"}
          </span>
        </div>
        <p className="text-gray-500 text-sm mb-2">
          {result.fileName} &bull; {formatTimestamp(result.timestamp)}
        </p>
        {isFinal && assessorReviewedAt && (
          <p className="font-serif text-xs italic text-gray-500">
            Reviewed by your assessor on{" "}
            {new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" }).format(assessorReviewedAt)}
          </p>
        )}
        {!isFinal && (
          <p className="text-gray-400 text-xs">
            This is an AI-generated assessment. Final grades are provided by human reviewers.
          </p>
        )}
        {result.submissionId !== undefined && (
          <button
            type="button"
            onClick={async () => {
              const url = await api.getSubmissionFileUrl(result.submissionId!);
              window.open(url, "_blank");
            }}
            className="inline-flex items-center gap-1.5 text-teal-600 hover:text-teal-700 text-sm transition-colors mt-2"
          >
            <FileDown className="w-4 h-4" />
            View Document
          </button>
        )}
      </div>

      {/* Relevance Warning - Show if not relevant */}
      {!result.isRelevant && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-amber-800 font-medium mb-1">
                Article Not Relevant to Work Package
              </h3>
              <p className="text-gray-700 text-sm mb-2">{result.relevanceExplanation}</p>
            </div>
          </div>
        </div>
      )}

      {/* Overall Score - only shown after human review */}
      {isFinal ? (
        <div className="text-center mb-8">
          <div className="text-7xl font-bold text-gray-900 mb-1">{result.overallScore}</div>
          <div className="text-gray-400 text-lg mb-2">/100</div>
          <div className={`text-lg font-medium ${getScoreColorClass(result.overallScore)}`}>
            {getScoreLabel(result.overallScore)}
          </div>
        </div>
      ) : (
        <div className="mb-8 p-4 bg-blue-50 border border-blue-100 rounded-xl">
          <p className="text-sm text-blue-700 text-center">
            Your feedback is ready. Your final result will appear once your assessor has reviewed
            and confirmed it.
          </p>
        </div>
      )}

      {/* Sections */}
      <div className="space-y-3">{result.sections?.map(renderSection)}</div>
    </div>
  );
}
